import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Car, AlertCircle, CheckCircle, Loader, Hash, Calendar, Fuel, Settings, Palette, X, HelpCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import CookieBanner from './CookieBanner';

function App() {
  const [formData, setFormData] = useState({
    numberPlate: '',
    mileage: '',
    email: '',
    postcode: '',
    mobile: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [contactPreference, setContactPreference] = useState(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationFadeOut, setNotificationFadeOut] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // helper to send events to Google Analytics if available
  const trackEvent = (eventName) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        'event_category': 'engagement'
      });
    }
  };




  const carouselItems = [
    'Instant cash offer',
    'Contact within 24 hours',
    'Fast free collection',
    'Instant payment',
    'We handle the paperwork',
    'Finance cleared',
    'Money in your account as soon as today'
  ];
  const valuationRef = useRef(null);
  const vehicleDetailsRef = useRef(null);
  const hasTriggeredConfetti = useRef(false);

  const isValidUKPostcode = (postcode) => {
    const regex = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
    return regex.test(postcode.trim());
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const isValidUKPhone = (phone) => {
    const digits = phone.replace(/[\s\-()]/g, '');
    return /^(?:0[1-9]\d{8,9}|(?:\+44|0044)[1-9]\d{8,9})$/.test(digits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const errors = {};
    if (!formData.numberPlate) errors.numberPlate = 'Please enter a registration number';
    if (!formData.mileage) errors.mileage = 'Please enter the current mileage';

    // Validate additional fields if they're shown
    if (showAdditionalFields) {
      if (!formData.email) errors.email = 'Please enter an email address';
      else if (!isValidEmail(formData.email)) errors.email = 'Please enter a valid email address';
      if (!formData.postcode) errors.postcode = 'Please enter a postcode';
      else if (!isValidUKPostcode(formData.postcode)) errors.postcode = 'Please enter a valid UK postcode';
      if (!formData.mobile) errors.mobile = 'Please enter a mobile number';
      else if (!isValidUKPhone(formData.mobile)) errors.mobile = 'Please enter a valid UK phone number';
    }

    // If there are errors, set them and don't submit
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowNotification(true);
      setNotificationFadeOut(false);

      // Start fade-out after 3.5 seconds
      setTimeout(() => {
        setNotificationFadeOut(true);
      }, 3500);

      // Hide notification after fade-out completes (4 seconds total)
      setTimeout(() => {
        setShowNotification(false);
      }, 4000);
      return;
    }

    // validation succeeded, track conversion
    trackEvent('get_valuation_click');

    // Clear validation errors if all fields are valid
    setValidationErrors({});
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      // Call your backend API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/valuation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numberPlate: formData.numberPlate,
          mileage: parseInt(formData.mileage)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get valuation');
      }

      const data = await response.json();
      setResult({
        ...data,
        mileage: parseInt(formData.mileage)
      });

      // Save valuation data to Excel spreadsheet
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/save-valuation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            mileage: formData.mileage,
            email: formData.email,
            postcode: formData.postcode,
            mobile: formData.mobile
          },
          vehicle: data.vehicle,
          valuation: data.valuation
        })
      }).catch(err => console.error('Failed to save valuation:', err));
      
    } catch (err) {
      setError(err.message || 'Failed to retrieve valuation');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };


  // Confetti effect when valuation comes into view
  useEffect(() => {
    if (!valuationRef.current || !result || !result.valuation.displayPrice) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggeredConfetti.current) {
            hasTriggeredConfetti.current = true;

            // Trigger confetti from bottom of screen
            const count = 200;
            const defaults = {
              origin: { y: 1 }, // Start from bottom
              spread: 100,
              ticks: 300,
              gravity: 0.8,
              decay: 0.94,
              startVelocity: 45,
            };

            function fire(particleRatio, opts) {
              confetti({
                ...defaults,
                ...opts,
                particleCount: Math.floor(count * particleRatio),
              });
            }

            fire(0.25, { spread: 26, startVelocity: 55 });
            fire(0.2, { spread: 60 });
            fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
            fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
            fire(0.1, { spread: 120, startVelocity: 45 });
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    observer.observe(valuationRef.current);

    return () => {
      if (valuationRef.current) {
        observer.unobserve(valuationRef.current);
      }
    };
  }, [result]);

  // Reset confetti trigger when result changes
  useEffect(() => {
    hasTriggeredConfetti.current = false;
  }, [result]);

  // Auto-rotate carousel
  useEffect(() => {
    if (!result || !result.valuation.displayPrice) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselItems.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [result, carouselItems.length]);

  // Scroll to Vehicle Details when results are ready
  useEffect(() => {
    if (result && vehicleDetailsRef.current) {
      setTimeout(() => {
        vehicleDetailsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [result]);

  return (
    <>
      <CookieBanner />
      <style>{`
        @keyframes slideUp {
          from {
            transform: translate(-50%, 100px);
            opacity: 0;
          }
          to {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          to {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out forwards;
        }
        .animate-fade-out {
          animation: fadeOut 0.5s ease-out forwards;
        }
        @keyframes carouselFade {
          0% { opacity: 0; transform: translateY(10px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-carousel {
          animation: carouselFade 3s ease-in-out;
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .price-gradient {
          background: linear-gradient(90deg, #1e3a5f, #2563eb, #06b6d4, #2563eb, #1e3a5f);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 20s linear infinite;
        }
        .price-glow {
          filter: drop-shadow(0 0 20px rgba(37, 99, 235, 0.3));
        }
      `}</style>
      <div className="min-h-screen bg-white p-6">
      <div className="max-w-4xl mx-auto relative">
        {/* How It Works Modal */}
        {showHowItWorks && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHowItWorks(false)}>
            <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShowHowItWorks(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors"
              >
                <X size={24} />
              </button>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">How It Works</h2>
              <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
                <p>Send us your enquiry and we'll email you to confirm your offer. We'll then ask for a few details about your car's condition (service history, keys, bodywork, mechanical issues, etc.).</p>
                <p>Once we've reviewed everything, we'll confirm your final price — and if all is as described, there'll be no changes to your offer.</p>
                <p>We'll arrange a convenient collection date, take a short test drive, check your ID, and pay you by secure bank transfer.</p>
                <p className="font-semibold text-slate-900">Simple, straightforward, and stress-free.</p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12 pt-8">
          <div><img src="sell-my-car-teesside-logo.png" alt="Sell My Car Teesside Logo" className="mx-auto w-64 h-32 mt-6 mb-3 object-contain" /></div>
          <h1 className="text-5xl font-bold text-slate-900 mb-3 tracking-tight">
            Get your <span className="animate-bounce inline-block" style={{color: 'rgb(104 179 226)'}}>instant</span> car valuation
          </h1>
          <p className="text-slate-600 text-lg">
            {/* Instant market value based on registration and mileage */}
            We'll give you a local cash valuation in less than a minute.
          </p>
          <button
            onClick={() => setShowHowItWorks(true)}
            className="mt-6 inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 font-semibold text-sm transition-colors bg-slate-100 hover:bg-blue-50 px-4 py-2 rounded-full border border-slate-200 hover:border-blue-300"
          >
            <HelpCircle size={18} />
            How It Works
          </button>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Registration Number
              </label>
              <div className="relative flex items-stretch">
                {/* UK Flag Side Badge */}
                <div className="flex flex-col items-center justify-center bg-blue-600 border-4 border-r-0 border-black rounded-l-lg px-2 py-3 sm:px-3 sm:py-5">
                  <div className="text-xl sm:text-2xl mb-1">🇬🇧</div>
                  <div className="text-white text-xs font-bold">UK</div>
                </div>
                {/* Registration Input */}
                <input
                  type="text"
                  value={formData.numberPlate}
                  onChange={(e) => {
                    const newPlate = e.target.value.toUpperCase();
                    setFormData({...formData, numberPlate: newPlate});
                    // Clear validation error when user starts typing
                    if (validationErrors.numberPlate) {
                      setValidationErrors({...validationErrors, numberPlate: false});
                    }
                    // Show additional fields if both registration and mileage are entered
                    if (newPlate && formData.mileage) {
                      setShowAdditionalFields(true);
                    }
                  }}
                  placeholder="AB12 CDE"
                  className={`flex-1 min-w-0 px-3 py-3 sm:px-6 sm:py-5 bg-yellow-400 rounded-r-lg text-black text-xl sm:text-3xl font-bold placeholder-black/40 focus:outline-none transition-colors tracking-wider sm:tracking-widest text-center ${
                    validationErrors.numberPlate
                      ? 'border-4 border-red-500 focus:ring-2 focus:ring-red-500'
                      : 'border-4 border-black focus:ring-2 focus:ring-blue-500'
                  }`}
                  style={{ fontFamily: 'monospace, sans-serif', backgroundColor: '#facc15', border: '4px solid black' }}
                  required
                  maxLength={8}
                />
              </div>
              {validationErrors.numberPlate && (
                <p className="text-red-500 text-sm mt-2">{validationErrors.numberPlate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Current Mileage
              </label>
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => {
                  const newMileage = e.target.value;
                  setFormData({...formData, mileage: newMileage});
                  // Clear validation error when user starts typing
                  if (validationErrors.mileage) {
                    setValidationErrors({...validationErrors, mileage: false});
                  }
                  // Show additional fields if both registration and mileage are entered
                  if (formData.numberPlate && newMileage) {
                    setShowAdditionalFields(true);
                  }
                }}
                placeholder="e.g. 52000"
                className={`w-full px-6 py-4 bg-slate-50 rounded-xl text-slate-900 text-lg placeholder-slate-400 focus:outline-none transition-colors ${
                  validationErrors.mileage
                    ? 'border-2 border-red-500 focus:ring-2 focus:ring-red-500'
                    : 'border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                }`}
                required
              />
              {validationErrors.mileage && (
                <p className="text-red-500 text-sm mt-2">{validationErrors.mileage}</p>
              )}
            </div>

            {showAdditionalFields && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address <span className="text-slate-500 text-xs">(So we can get in touch if you want to sell)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      if (validationErrors.email) {
                        setValidationErrors({...validationErrors, email: false});
                      }
                    }}
                    placeholder="e.g. john@example.com"
                    className={`w-full px-6 py-4 bg-slate-50 rounded-xl text-slate-900 text-lg placeholder-slate-400 focus:outline-none transition-colors ${
                      validationErrors.email
                        ? 'border-2 border-red-500 focus:ring-2 focus:ring-red-500'
                        : 'border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    }`}
                    required
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-sm mt-2">{validationErrors.email}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Postcode <span className="text-slate-500 text-xs">(To make sure you're in our service area)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => {
                        setFormData({...formData, postcode: e.target.value.toUpperCase()});
                        if (validationErrors.postcode) {
                          setValidationErrors({...validationErrors, postcode: false});
                        }
                      }}
                      placeholder="e.g. TS1 2AB"
                      className={`w-full px-6 py-4 bg-slate-50 rounded-xl text-slate-900 text-lg placeholder-slate-400 focus:outline-none transition-colors ${
                        validationErrors.postcode
                          ? 'border-2 border-red-500 focus:ring-2 focus:ring-red-500'
                          : 'border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      }`}
                      required
                    />
                    {validationErrors.postcode && (
                      <p className="text-red-500 text-sm mt-2">{validationErrors.postcode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Mobile Number <span className="text-slate-500 text-xs">(So we can get in touch if you want to sell)</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => {
                        setFormData({...formData, mobile: e.target.value});
                        if (validationErrors.mobile) {
                          setValidationErrors({...validationErrors, mobile: false});
                        }
                      }}
                      placeholder="e.g. 07123 456789"
                      className={`w-full px-6 py-4 bg-slate-50 rounded-xl text-slate-900 text-lg placeholder-slate-400 focus:outline-none transition-colors ${
                        validationErrors.mobile
                          ? 'border-2 border-red-500 focus:ring-2 focus:ring-red-500'
                          : 'border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      }`}
                      required
                    />
                    {validationErrors.mobile && (
                      <p className="text-red-500 text-sm mt-2">{validationErrors.mobile}</p>
                    )}
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold py-4 px-8 rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="animate-spin" size={20} />
                  Retrieving Valuation...
                </span>
              ) : (
                'Get Valuation ✅'
              )}
            </button>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-300 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-red-800 font-semibold text-lg mb-1">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="space-y-6">
            {/* Vehicle Info */}
            <div ref={vehicleDetailsRef} className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="text-green-600" size={28} />
                <h2 className="text-2xl font-bold text-slate-900">Vehicle Details</h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Hash size={16} />
                    Registration
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.registration}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Car size={16} />
                    Make & Model
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.make} {result.vehicle.model}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Calendar size={16} />
                    Year
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.year}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Fuel size={16} />
                    Fuel Type
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.fuelType}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Settings size={16} />
                    Transmission
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.transmission}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm mb-1 flex items-center gap-2">
                    <Palette size={16} />
                    Colour
                  </p>
                  <p className="text-slate-900 font-bold text-lg">{result.vehicle.colour}</p>
                </div>
              </div>
            </div>

            {/* Valuation */}
            <div ref={valuationRef} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 shadow-xl border border-blue-200">
              <div className="text-center">
                {result.valuation.displayPrice ? (
                  <>
                    <p className="text-blue-700 text-sm font-semibold uppercase tracking-wide mb-3">Estimated Value</p>
                    <div className="price-glow mb-4">
                      <p className="text-6xl font-extrabold price-gradient tracking-tight">
                        {formatCurrency(result.valuation.displayPrice)}
                      </p>
                    </div>
                    <div className="h-8 flex items-center justify-center">
                      <p key={carouselIndex} className="text-blue-600 font-semibold text-lg animate-carousel">
                        {carouselItems[carouselIndex]}
                      </p>
                    </div>
                    {/* <div className="inline-block border border-slate-300 rounded-xl px-6 py-3 bg-white/60 max-w-md mx-auto">
                      <p className="text-slate-500 text-sm">This valuation is an estimate based on market data and the provided mileage. Actual trade-in or sale prices may vary based on vehicle condition, service history, and local market demand.</p>
                    </div> */}
                  </>
                ) : (
                  <>
                    <AlertCircle className="text-amber-500 mx-auto mb-3" size={36} />
                    <p className="text-slate-900 font-bold text-xl mb-2">Valuation isn't currently available</p>
                    <p className="text-slate-600 text-sm mb-3">Due to the vehicle's age and mileage, we're unable to provide an instant online valuation.</p>
                    <p className="text-slate-700 text-sm font-semibold">We'd still be happy to make you an offer — get in touch and we'll come back to you with a price.</p>
                  </>
                )}
              </div>

            </div>

            {/* Contact Preference */}
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200">
              {contactSubmitted ? (
                <div className="text-center py-6">
                  <CheckCircle className="text-green-600 mx-auto mb-4" size={48} />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    Thanks for enquiring
                  </h3>
                  <p className="text-slate-600">
                    A member of the team will be in touch within 24 hours.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center">
                    How would you like to proceed?
                  </h3>
                  <p className="text-slate-600 text-center mb-2">
                    Select your preferred method of contact
                  </p>
                  <ul className="text-slate-500 text-sm text-center mb-6 space-y-1">
                    <li>✅ No hidden fees</li>
                    <li>✅ No obligation</li>
                    <li>✅ We handle the paperwork</li>
                    <li>✅ Fast and secure instant bank transfer</li>
                  </ul>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <button
                      onClick={() => {
                        setContactPreference(contactPreference === 'email' ? null : 'email');
                      }}
                      className={`py-4 px-6 rounded-xl font-semibold transition-colors ${
                        contactPreference === 'email'
                          ? 'bg-blue-600 text-white border-2 border-blue-400'
                          : 'bg-slate-100 text-slate-700 border-2 border-slate-300 hover:border-blue-500'
                      }`}
                    >
                      📧 Contact by Email
                    </button>

                    <button
                      onClick={() => {
                        setContactPreference(contactPreference === 'phone' ? null : 'phone');
                      }}
                      className={`py-4 px-6 rounded-xl font-semibold transition-colors ${
                        contactPreference === 'phone'
                          ? 'bg-blue-600 text-white border-2 border-blue-400'
                          : 'bg-slate-100 text-slate-700 border-2 border-slate-300 hover:border-blue-500'
                      }`}
                    >
                      📱 Contact by Phone
                    </button>
                  </div>

                  {contactPreference && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => {
                          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/send-contact`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              user: {
                                mileage: formData.mileage,
                                email: formData.email,
                                postcode: formData.postcode,
                                mobile: formData.mobile
                              },
                              vehicle: result.vehicle,
                              valuation: result.valuation,
                              contactPreference
                            })
                          }).catch(err => console.error('Failed to send contact email:', err));
                          // track sell button click
                          trackEvent('sell_my_car_click');
                          // fire Google Ads conversion event
                          if (window.gtag) {
                            window.gtag('event', 'conversion', {
                              'send_to': 'AW-17975936696/1LEJCPDTzoIcELiNzPtC',
                              'value': 1.0,
                              'currency': 'GBP'
                            });
                          }
                          setContactSubmitted(true);
                        }}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold py-3 px-8 rounded-xl transition-transform hover:scale-105 shadow-lg"
                      >
                        Sell My Car
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Disclaimer */}
            {/* <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <p className="text-slate-600 text-sm text-center">
                This valuation is an estimate based on market data and the provided mileage.
                Actual trade-in or sale prices may vary based on vehicle condition, service history,
                and local market demand.
              </p>
            </div> */}
          </div>
        )}

        {/* Trustpilot Reviews Section */}
        <div className="mt-12 bg-white rounded-3xl p-8 shadow-xl border border-slate-200">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Trusted by hundreds of customers
            </h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-8 h-8 fill-green-500"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
            </div>
            <p className="text-slate-700 font-semibold text-lg mb-1">
              Excellent
            </p>
            <p className="text-slate-600 text-sm mb-4">
              Based on <span className="font-semibold">50+ reviews</span>
            </p>
            <div className="flex items-center justify-center gap-2">
              {/* Trustpilot Logo */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1132.8 278.2" width="100"><path d="M297.7 98.6h114.7V120h-45.1v120.3h-24.8V120h-44.9V98.6zm109.8 39.1h21.2v19.8h.4c.7-2.8 2-5.5 3.9-8.1 1.9-2.6 4.2-5.1 6.9-7.2 2.7-2.2 5.7-3.9 9-5.3 3.3-1.3 6.7-2 10.1-2 2.6 0 4.5.1 5.5.2s2 .3 3.1.4v21.8c-1.6-.3-3.2-.5-4.9-.7-1.7-.2-3.3-.3-4.9-.3-3.8 0-7.4.8-10.8 2.3-3.4 1.5-6.3 3.8-8.8 6.7-2.5 3-4.5 6.6-6 11s-2.2 9.4-2.2 15.1v48.8h-22.6V137.7zm164 102.6h-22.2V226h-.4c-2.8 5.2-6.9 9.3-12.4 12.4-5.5 3.1-11.1 4.7-16.8 4.7-13.5 0-23.3-3.3-29.3-10s-9-16.8-9-30.3v-65.1H504v62.9c0 9 1.7 15.4 5.2 19.1 3.4 3.7 8.3 5.6 14.5 5.6 4.8 0 8.7-.7 11.9-2.2 3.2-1.5 5.8-3.4 7.7-5.9 2-2.4 3.4-5.4 4.3-8.8.9-3.4 1.3-7.1 1.3-11.1v-59.5h22.6v102.5zm38.5-32.9c.7 6.6 3.2 11.2 7.5 13.9 4.4 2.6 9.6 4 15.7 4 2.1 0 4.5-.2 7.2-.5s5.3-1 7.6-1.9c2.4-.9 4.3-2.3 5.9-4.1 1.5-1.8 2.2-4.1 2.1-7-.1-2.9-1.2-5.3-3.2-7.1-2-1.9-4.5-3.3-7.6-4.5-3.1-1.1-6.6-2.1-10.6-2.9-4-.8-8-1.7-12.1-2.6-4.2-.9-8.3-2.1-12.2-3.4-3.9-1.3-7.4-3.1-10.5-5.4-3.1-2.2-5.6-5.1-7.4-8.6-1.9-3.5-2.8-7.8-2.8-13 0-5.6 1.4-10.2 4.1-14 2.7-3.8 6.2-6.8 10.3-9.1 4.2-2.3 8.8-3.9 13.9-4.9 5.1-.9 10-1.4 14.6-1.4 5.3 0 10.4.6 15.2 1.7 4.8 1.1 9.2 2.9 13.1 5.5 3.9 2.5 7.1 5.8 9.7 9.8 2.6 4 4.2 8.9 4.9 14.6h-23.6c-1.1-5.4-3.5-9.1-7.4-10.9-3.9-1.9-8.4-2.8-13.4-2.8-1.6 0-3.5.1-5.7.4-2.2.3-4.2.8-6.2 1.5-1.9.7-3.5 1.8-4.9 3.2-1.3 1.4-2 3.2-2 5.5 0 2.8 1 5 2.9 6.7 1.9 1.7 4.4 3.1 7.5 4.3 3.1 1.1 6.6 2.1 10.6 2.9 4 .8 8.1 1.7 12.3 2.6 4.1.9 8.1 2.1 12.1 3.4 4 1.3 7.5 3.1 10.6 5.4 3.1 2.3 5.6 5.1 7.5 8.5 1.9 3.4 2.9 7.7 2.9 12.7 0 6.1-1.4 11.2-4.2 15.5-2.8 4.2-6.4 7.7-10.8 10.3-4.4 2.6-9.4 4.6-14.8 5.8-5.4 1.2-10.8 1.8-16.1 1.8-6.5 0-12.5-.7-18-2.2-5.5-1.5-10.3-3.7-14.3-6.6-4-3-7.2-6.7-9.5-11.1-2.3-4.4-3.5-9.7-3.7-15.8H610zm74.6-69.7h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1zm76.1 0h21.4v13.9h.4c3.2-6 7.6-10.2 13.3-12.8 5.7-2.6 11.8-3.9 18.5-3.9 8.1 0 15.1 1.4 21.1 4.3 6 2.8 11 6.7 15 11.7 4 5 6.9 10.8 8.9 17.4 2 6.6 3 13.7 3 21.2 0 6.9-.9 13.6-2.7 20-1.8 6.5-4.5 12.2-8.1 17.2-3.6 5-8.2 8.9-13.8 11.9-5.6 3-12.1 4.5-19.7 4.5-3.3 0-6.6-.3-9.9-.9-3.3-.6-6.5-1.6-9.5-2.9-3-1.3-5.9-3-8.4-5.1-2.6-2.1-4.7-4.5-6.5-7.2h-.4v51.2h-22.6V137.7zm79 51.4c0-4.6-.6-9.1-1.8-13.5-1.2-4.4-3-8.2-5.4-11.6-2.4-3.4-5.4-6.1-8.9-8.1-3.6-2-7.7-3.1-12.3-3.1-9.5 0-16.7 3.3-21.5 9.9-4.8 6.6-7.2 15.4-7.2 26.4 0 5.2.6 10 1.9 14.4 1.3 4.4 3.1 8.2 5.7 11.4 2.5 3.2 5.5 5.7 9 7.5 3.5 1.9 7.6 2.8 12.2 2.8 5.2 0 9.5-1.1 13.1-3.2 3.6-2.1 6.5-4.9 8.8-8.2 2.3-3.4 4-7.2 5-11.5.9-4.3 1.4-8.7 1.4-13.2zm39.9-90.5h22.6V120h-22.6V98.6zm0 39.1h22.6v102.6h-22.6V137.7zm42.8-39.1H945v141.7h-22.6V98.6zm91.9 144.5c-8.2 0-15.5-1.4-21.9-4.1-6.4-2.7-11.8-6.5-16.3-11.2-4.4-4.8-7.8-10.5-10.1-17.1-2.3-6.6-3.5-13.9-3.5-21.8 0-7.8 1.2-15 3.5-21.6 2.3-6.6 5.7-12.3 10.1-17.1 4.4-4.8 9.9-8.5 16.3-11.2 6.4-2.7 13.7-4.1 21.9-4.1s15.5 1.4 21.9 4.1c6.4 2.7 11.8 6.5 16.3 11.2 4.4 4.8 7.8 10.5 10.1 17.1 2.3 6.6 3.5 13.8 3.5 21.6 0 7.9-1.2 15.2-3.5 21.8-2.3 6.6-5.7 12.3-10.1 17.1-4.4 4.8-9.9 8.5-16.3 11.2-6.4 2.7-13.7 4.1-21.9 4.1zm0-17.9c5 0 9.4-1.1 13.1-3.2 3.7-2.1 6.7-4.9 9.1-8.3 2.4-3.4 4.1-7.3 5.3-11.6 1.1-4.3 1.7-8.7 1.7-13.2 0-4.4-.6-8.7-1.7-13.1s-2.9-8.2-5.3-11.6c-2.4-3.4-5.4-6.1-9.1-8.2-3.7-2.1-8.1-3.2-13.1-3.2s-9.4 1.1-13.1 3.2c-3.7 2.1-6.7 4.9-9.1 8.2-2.4 3.4-4.1 7.2-5.3 11.6-1.1 4.4-1.7 8.7-1.7 13.1 0 4.5.6 8.9 1.7 13.2 1.1 4.3 2.9 8.2 5.3 11.6 2.4 3.4 5.4 6.2 9.1 8.3 3.7 2.2 8.1 3.2 13.1 3.2zm58.4-87.5h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1z" fill="#191919"/><path fill="#00b67a" d="M271.3 98.6H167.7L135.7 0l-32.1 98.6L0 98.5l83.9 61L51.8 258l83.9-60.9 83.8 60.9-32-98.5 83.8-60.9z"/><path fill="#005128" d="M194.7 181.8l-7.2-22.3-51.8 37.6z"/></svg>
            </div>
          </div>

          {/* Sample Reviews */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-5 h-5 fill-green-500"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 text-sm mb-3">
                "Sold my car to TeesideAutos - the buyers were polite and friendly and gave me a good price for my vehicle - would recommend them to anyone - good luck for the future … many thanks "
              </p>
              <div className="flex items-center gap-3 mt-4">
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">KH</div>
                <p className="text-slate-500 text-xs font-semibold">KH, verified customer</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-5 h-5 fill-green-500"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 text-sm mb-3">
                "Just sold our car, to Teesideautos. What a great, uncomplicated, friendly service we received. Elliot even collected the car. There wasn't any hassle or mark down of price, of that agreed online and funds were transferred before he took the  car away, which gave peace of mind. Would definitely recommend Teesideautos. Excellent service and communication."
              </p>
              <div className="flex items-center gap-3 mt-4">
                <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">K</div>
                <p className="text-slate-500 text-xs font-semibold">Karen, verified customer</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-5 h-5 fill-green-500"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 text-sm mb-3">
                "Great communication from start to finish, Tom was a pleasure to deal with, got a good a car at a great price"
              </p>
              <div className="flex items-center gap-3 mt-4">
                <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">GJ</div>
                <p className="text-slate-500 text-xs font-semibold">G Jacobs, verified customer</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Toast */}
        {showNotification && (
          <div
            className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 cursor-pointer ${notificationFadeOut ? 'animate-fade-out' : 'animate-slide-up'}`}
            onClick={() => {
              setNotificationFadeOut(true);
              setTimeout(() => {
                setShowNotification(false);
              }, 500);
            }}
          >
            <div className="bg-red-600 text-white px-8 py-4 rounded-xl shadow-2xl flex items-center gap-3 min-w-[320px]">
              <AlertCircle size={24} className="flex-shrink-0" />
              <div>
                <p className="font-semibold text-lg">Missing Information</p>
                <p className="text-sm text-red-100">Please fill in all mandatory fields</p>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Footer */}
        <div className="text-center py-6 mt-8">
          <div className="flex items-center justify-center gap-4 mb-2">
            <Link to="/terms-and-conditions" className="text-slate-500 hover:text-blue-600 text-sm transition-colors">Terms & Conditions</Link>
            <span className="text-slate-300">|</span>
            <Link to="/privacy-policy" className="text-slate-500 hover:text-blue-600 text-sm transition-colors">Privacy Policy</Link>
            <span className="text-slate-300">|</span>
            <Link to="/cookies-policy" className="text-slate-500 hover:text-blue-600 text-sm transition-colors">Cookies Policy</Link>
          </div>
          <p className="text-slate-500 text-sm">
            Powered by <a href="https://www.teessideautos.co.uk/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TeessideAutos</a>
          </p>
        </div>
    </div>
    </>
  );
}

export default App;
